import { decamelizeKeys } from 'humps';

import { apiClient } from '../apiClient';

export const PATHS = {
  PROGRAM_INSTANCE: '/api/v4/program-instance/',
  PRESCRIBE_PROGRAMS: '/api/v1/prescribe-programs/',
  BEHAVIOR: '/api/v4/behavior/',
} as const;

// A program the user is enrolled in. We need to know whether *any* exist
// (Sparta's checkProgramEnrollment gate) and whether the backend auto-assigned
// a coach — group-modality enrollment returns `assignedCoachUuid`, which is
// Sparta's `autoAssignedCoach` signal for skipping coach matching entirely.
export type ProgramInstance = {
  uuid: string;
  assignedCoachUuid?: string;
};

type ProgramInstancesResponse =
  | { objects?: ProgramInstance[]; items?: ProgramInstance[] }
  | ProgramInstance[];

// Mirrors Sparta's getUserProgramInstances: GET /api/v4/program-instance/ keyed
// by the user's resource URI. Used to gate the static survey — a user with no
// program instance starts /step/health-conditions; one with a program has
// finished the survey portion and goes on to coach matching.
export async function fetchUserProgramInstances(
  userUri: string,
  signal?: AbortSignal
): Promise<ProgramInstance[]> {
  const res = await apiClient.get<ProgramInstancesResponse>(PATHS.PROGRAM_INSTANCE, {
    params: { user_uri: userUri },
    signal,
  });
  const data = res.data;
  if (Array.isArray(data)) return data;
  return data?.objects ?? data?.items ?? [];
}

// A skill/behavior a program focuses on (Sparta's behavior entity). The review
// screen renders these as the "What you'll work on" grid (title + icon).
export type ProgramBehavior = {
  uuid: string;
  title: string;
  icon: string | null;
};

type BehaviorsResponse =
  | { objects?: ProgramBehavior[]; items?: ProgramBehavior[] }
  | ProgramBehavior[];

// Mirrors Sparta's getBehaviors: GET /api/v4/behavior/?program_urn=<urn>. Returns
// the behaviors/skills a program targets, shown on the program review screens.
export async function fetchProgramBehaviors(
  programUrn: string,
  signal?: AbortSignal
): Promise<ProgramBehavior[]> {
  const res = await apiClient.get<BehaviorsResponse>(PATHS.BEHAVIOR, {
    params: decamelizeKeys({ programUrn }),
    signal,
  });
  const data = res.data;
  const list = Array.isArray(data) ? data : (data?.objects ?? data?.items ?? []);
  return list.map((b) => ({ uuid: b.uuid, title: b.title, icon: b.icon ?? null }));
}

// A program the user can choose during program matching. Fields are the subset
// the onboarding screens render; the wire carries more.
export type Program = {
  uuid: string;
  urn: string;
  title: string;
  description: string;
  overviewDescription: string | null;
  // Detail copy (markdown: prose paragraphs + a feature bullet list). The review
  // screen shows `detailDescription` for individual coaching and
  // `groupDetailDescription` for group coaching.
  detailDescription: string | null;
  groupDetailDescription: string | null;
  // Acknowledgements the user confirms by joining (markdown bullet list).
  disclaimer: string | null;
  image: string | null;
  icon: string | null;
  category: string;
  internalName: string;
};

// Per-program coaching metadata (Sparta's programMetaInfo), keyed by a
// dash-stripped program URN. Drives whether the coaching-method screen shows.
export type ProgramMetaInfo = {
  isGroup: boolean;
  isIndividual: boolean;
  prescribedCoachingMethod: 'individual' | 'group' | null;
  cadenceDescription?: string;
};

export type ProgramRecommendation = {
  programUuid: string;
  score: number;
  insight?: string;
};

export type PrescribedPrograms = {
  programs: Program[];
  recommendationScores: ProgramRecommendation[];
  programMetaInfo: Record<string, ProgramMetaInfo>;
  // goStraightToDetailsPage → show the primary-recommendation detail screen
  // first; additionalChoicesAllowed → let the user browse other programs.
  additionalChoicesAllowed: boolean;
  goStraightToDetailsPage: boolean;
};

type PrescribeProgramsWire = Partial<PrescribedPrograms> & {
  items?: Program[];
};

// `program_meta_info` arrives keyed by the program URN (`urn:vida:program:<uuid>`),
// but the humps response interceptor camelizes EVERY object key — so those keys
// reach us mangled (the uuid's dashes are stripped and the following char is
// upper-cased, e.g. `…4b77B124…`). Rather than depend on that fragile shape, we
// canonicalize: lowercase + keep only alphanumerics. Both a program's `urn` value
// (untouched — humps only rewrites keys) and the mangled meta key reduce to the
// same string, so we can match them reliably.
const canonicalizeUrnKey = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '');

// Mirrors Sparta's getPrescribedPrograms: GET /api/v1/prescribe-programs/ keyed
// by the user's URN. Returns the program list + recommendation scores + the
// flags that decide the program-matching entry screen (full list vs primary
// recommendation detail). `programMetaInfo` is RE-KEYED here by each program's
// canonical uuid (see `programMetaInfoKey`) so screens can look meta up by uuid.
export async function fetchPrescribedPrograms(
  userUrn: string,
  signal?: AbortSignal
): Promise<PrescribedPrograms> {
  const res = await apiClient.get<PrescribeProgramsWire>(PATHS.PRESCRIBE_PROGRAMS, {
    params: decamelizeKeys({ userUrn }),
    signal,
  });
  const data = res.data ?? {};
  const programs = data.programs ?? data.items ?? [];

  const rawMeta = Object.entries(data.programMetaInfo ?? {}).map(
    ([key, value]) => [canonicalizeUrnKey(key), value] as const
  );
  const programMetaInfo: Record<string, ProgramMetaInfo> = {};
  for (const program of programs) {
    if (!program.uuid) continue;
    const canonicalUuid = canonicalizeUrnKey(program.uuid);
    // The meta key is `<urn-prefix><uuid>`; canonicalized it ends with the uuid.
    const match = rawMeta.find(([key]) => key.endsWith(canonicalUuid));
    if (match) programMetaInfo[programMetaInfoKey(program.uuid)] = match[1];
  }

  return {
    programs,
    recommendationScores: data.recommendationScores ?? [],
    programMetaInfo,
    additionalChoicesAllowed: data.additionalChoicesAllowed ?? false,
    goStraightToDetailsPage: data.goStraightToDetailsPage ?? false,
  };
}

// Canonical key `fetchPrescribedPrograms` stores `programMetaInfo` under, and that
// screens use to look a program's meta up by its uuid. Lowercase + alphanumerics
// only, so it's stable regardless of how the wire keys the meta.
export function programMetaInfoKey(programUuid: string): string {
  return canonicalizeUrnKey(programUuid);
}

// The recommended program is the highest-scored recommendation (Sparta's
// getHighestRecommendedProgramRec). Returns undefined when there are no scores.
export function highestRecommendedProgramUuid(
  catalog: PrescribedPrograms | undefined
): string | undefined {
  const scores = catalog?.recommendationScores ?? [];
  if (scores.length === 0) return undefined;
  return scores.reduce((best, s) => (s.score > best.score ? s : best)).programUuid;
}

/**
 * Orders programs for the list by recommendation score, highest first. Programs
 * with no recommendation score sink to the bottom; ties keep their backend
 * response order (stable sort). The screen surfaces the top-scored program into
 * its own "Recommended for you" section and lists the rest in this order.
 */
export function orderProgramsByScore(catalog: PrescribedPrograms | undefined): Program[] {
  const programs = catalog?.programs ?? [];
  const scoreByUuid = new Map(
    (catalog?.recommendationScores ?? []).map((r) => [r.programUuid, r.score])
  );
  const score = (uuid: string) => scoreByUuid.get(uuid) ?? -Infinity;
  return [...programs].sort((a, b) => score(b.uuid) - score(a.uuid));
}

export type CreateProgramInstancePayload = {
  programUuid: string;
  userUuid: string;
  teamCode: string;
  coachingModality?: 'individual' | 'group';
};

// Enrolls the user in the chosen program (Sparta's postProgramInstance):
// POST /api/v4/program-instance/. The backend assigns the program instance (and
// may auto-assign a coach). The rules engine flips its `hasProgram` flag from
// this call's success (no user re-pull needed — we just created the instance)
// and routes on to coach matching.
export async function createProgramInstance(
  payload: CreateProgramInstancePayload,
  signal?: AbortSignal
): Promise<ProgramInstance | undefined> {
  const res = await apiClient.post<ProgramInstance | { objects?: ProgramInstance[] }>(
    PATHS.PROGRAM_INSTANCE,
    decamelizeKeys(payload),
    { signal }
  );
  const data = res.data;
  if (!data) return undefined;
  if ('objects' in data) return data.objects?.[0];
  return data as ProgramInstance;
}
